import { useEffect, useState } from "react";
import { Download, X, Share, Plus } from "lucide-react";
import { useSession } from "@/lib/auth/auth-client";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

/** True when the app is already running as an installed PWA. */
function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS Safari
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

function isIos(): boolean {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent;
  return /iphone|ipad|ipod/i.test(ua);
}

/**
 * Prompts logged-in users to install the app whenever it is not already
 * installed. Shows the native install prompt on Android/Chrome and manual
 * "Add to Home Screen" instructions on iOS (which has no native prompt).
 *
 * The banner reappears on every new browser session until the app is
 * installed, so re-downloading after a delete always re-prompts.
 */
export default function InstallAppPrompt() {
  const { isAuthenticated } = useSession();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(true); // assume installed until we confirm otherwise
  const [dismissed, setDismissed] = useState(false);
  const [iosHelp, setIosHelp] = useState(false);

  useEffect(() => {
    setInstalled(isStandalone());

    // Reappear each session: dismissal is stored per-session only.
    try {
      setDismissed(sessionStorage.getItem("db-install-dismissed") === "1");
    } catch {
      /* ignore */
    }

    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);

    const mq = window.matchMedia?.("(display-mode: standalone)");
    const onDisplayChange = () => setInstalled(isStandalone());
    mq?.addEventListener?.("change", onDisplayChange);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      mq?.removeEventListener?.("change", onDisplayChange);
    };
  }, []);

  // Only prompt logged-in users who don't already have the app installed.
  const ios = isIos();
  const visible =
    isAuthenticated && !installed && !dismissed && (!!deferredPrompt || ios);

  if (!visible) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem("db-install-dismissed", "1");
    } catch {
      /* ignore */
    }
  };

  const handleInstall = async () => {
    if (ios) {
      setIosHelp((v) => !v);
      return;
    }
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    if (choice.outcome === "accepted") {
      setInstalled(true);
    }
    setDeferredPrompt(null);
  };

  return (
    <div className="fixed inset-x-0 bottom-0 z-[60] p-3 sm:p-4 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
      <div className="mx-auto max-w-md rounded-2xl border border-border bg-card text-card-foreground shadow-2xl">
        <div className="flex items-start gap-3 p-4">
          <div className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Download className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold">Install the Digitally Baffled app</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Add it to your home screen for quick access and notifications.
            </p>

            {ios && iosHelp && (
              <ol className="mt-3 space-y-1.5 text-xs text-muted-foreground">
                <li className="flex items-center gap-1.5">
                  1. Tap the Share icon
                  <Share className="h-3.5 w-3.5" />
                  in the toolbar
                </li>
                <li className="flex items-center gap-1.5">
                  2. Choose “Add to Home Screen”
                  <Plus className="h-3.5 w-3.5" />
                </li>
                <li>3. Tap “Add” to finish</li>
              </ol>
            )}

            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={handleInstall}
                className="inline-flex items-center justify-center rounded-lg bg-primary px-3 py-2 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90"
              >
                {ios ? (iosHelp ? "Hide steps" : "How to install") : "Install app"}
              </button>
              <button
                type="button"
                onClick={dismiss}
                className="inline-flex items-center justify-center rounded-lg border border-input bg-background px-3 py-2 text-xs font-medium text-foreground transition-colors hover:bg-accent"
              >
                Not now
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss install prompt"
            className="flex-none rounded-md p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
